import React from 'react';
import { X } from 'lucide-react';
import { Upgrades, UpgradeType } from '../types';
import { UPGRADE_TYPES } from '../constants';

interface UpgradesModalProps {
  showUpgrades: boolean;
  setShowUpgrades: (show: boolean) => void;
  upgrades: Upgrades;
  money: number;
  buyUpgrade: (upgradeId: string) => void;
  formatCurrency: (amount: number, decimals?: number) => string;
}

const UpgradesModal: React.FC<UpgradesModalProps> = ({
  showUpgrades,
  setShowUpgrades,
  upgrades,
  money,
  buyUpgrade,
  formatCurrency
}) => {
  if (!showUpgrades) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Research & Development</h2>
          <button
            onClick={() => setShowUpgrades(false)}
            className="text-gray-400 hover:text-white text-2xl"
          >
            <X size={24} />
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {UPGRADE_TYPES.map((upgrade: UpgradeType) => {
            const currentLevel = upgrades[upgrade.id];
            const cost = upgrade.baseCost * Math.pow(2, currentLevel);
            
            return (
              <div key={upgrade.id} className="bg-gray-700 p-4 rounded-lg">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-2">{upgrade.name}</h3>
                    <p className="text-sm text-gray-400 mb-2">{upgrade.description}</p>
                    <div className="text-xs text-gray-500">
                      <span>Current Level: </span>
                      <span className="font-bold text-blue-400">{currentLevel}</span>
                    </div>
                    {currentLevel > 0 && (
                      <div className="text-xs text-green-400 mt-1">
                        {upgrade.id === 'powerEfficiency' && `${(currentLevel * 10)}% power reduction`}
                        {upgrade.id === 'coolingEfficiency' && `${(currentLevel * 10)}% cooling reduction`}
                        {upgrade.id === 'serverPerformance' && `${(currentLevel * 15)}% revenue`}
                        {upgrade.id === 'networkInfrastructure' && `${(currentLevel * 20)}% customers`}
                        {upgrade.id === 'businessDevelopment' && `${(currentLevel * 50)}% reputation growth`}
                        {upgrade.id === 'ispOptimization' && `${(currentLevel * 25)}% ISP revenue`}
                        {upgrade.id === 'disasterPreparedness' && `${(currentLevel * 30)}% event damage reduction`}
                      </div>
                    )}
                  </div>
                  <div className="mt-3">
                    <button
                      onClick={() => buyUpgrade(upgrade.id)}
                      disabled={money < cost}
                      className={`px-3 py-1 text-xs rounded ${money < cost ? 'bg-gray-600 text-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}
                    >
                      Upgrade<br />{formatCurrency(cost)}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default UpgradesModal;
